package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.correctBorder
import com.shadowings.gero.presentation.ui.keyboardDisabledContainer
import com.shadowings.gero.presentation.ui.keyboardEnabledContainer
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.util.DevicePreviews

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun HieroKeyboardPreview() {

    AppTheme {
        Surface(
            Modifier.fillMaxSize()
        ) {
            HieroKeyboard(
                onClick = {},
                selected = "A",
                enabled = false,
                modifier = Modifier
            )
        }
    }
}

@Composable
fun HieroKeyboard(
    onClick: (String) -> Unit,
    selected: String?,
    modifier: Modifier,
    enabled: Boolean,
    keyHeight: androidx.compose.ui.unit.Dp = 64.dp
) {
    val hapticFeedback = LocalHapticFeedback.current
    val values = """
        q
        w
        ꜥ
        r
        t
        ṯ
        y
        i
        p
        ꜣ
        s
        d
        ḏ
        f
        g
        h
        k
        -
        š
        ḫ
        ẖ
        ḥ
        b
        n
        m
        .
        ⌫
    """.trimIndent().split("\n").map { it.trim() }

    val firstRow = values.take(9)
    val secondRow = values.drop(9).take(9)
    val thirdRow = values.drop(18)


    val rows = listOf(firstRow, secondRow, thirdRow)
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        rows.forEach { row ->
            Row {
                row.forEach {
                    Box(Modifier.padding(2.dp).weight(1f)) {
                        OutlinedCard(
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                onClick(it)
                            },
                            enabled = enabled,
                            shape = RoundedCornerShape(8.dp),
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = if (enabled) {
                                    if (selected.equals(it, ignoreCase = true)) {
                                        quizSelectionCorrectBg
                                    } else {
                                        keyboardEnabledContainer
                                    }
                                } else {
                                    keyboardDisabledContainer
                                },
                                contentColor = if (enabled) {
                                    if (selected == it) {
                                        correctBorder
                                    } else {
                                        MaterialTheme.colorScheme.onSurface
                                    }
                                } else {
                                    MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                                },
                                disabledContainerColor = keyboardDisabledContainer,
                                disabledContentColor = MaterialTheme.colorScheme.onSurface.copy(
                                    alpha = 0.38f
                                )
                            ),
                            border = BorderStroke(
                                width = if (selected == it) 2.dp else 1.dp,
                                color = if (selected == it) {
                                    correctBorder
                                } else {
                                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                }
                            ),
                            modifier = Modifier.fillMaxWidth().height(keyHeight)
                                .semantics { testTag = it }
                        ) {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    modifier = Modifier,
                                    text = it,
                                    // Body, not title: displayFontFamily (Roboto Condensed) is
                                    // missing several Egyptological transliteration glyphs on web.
                                    style = MaterialTheme.typography.bodyLarge.copy(
                                        fontSize = 26.sp,
                                        fontWeight = FontWeight.Light
                                    ),
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }
            }
        }
        val isSpaceSelected = selected == " " || selected == "␣"
        val spaceHeight = if (keyHeight > 48.dp) 44.dp else keyHeight
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 2.dp),
            horizontalArrangement = Arrangement.Center
        ) {
            Box(Modifier.fillMaxWidth(0.5f).padding(2.dp)) {
                OutlinedCard(
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        onClick(" ")
                    },
                    enabled = enabled,
                    shape = RoundedCornerShape(8.dp),
                    colors = CardDefaults.outlinedCardColors(
                        containerColor = if (enabled) {
                            if (isSpaceSelected) quizSelectionCorrectBg else keyboardEnabledContainer
                        } else {
                            keyboardDisabledContainer
                        },
                        contentColor = if (enabled) {
                            if (isSpaceSelected) correctBorder else MaterialTheme.colorScheme.onSurface
                        } else {
                            MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                        },
                        disabledContainerColor = keyboardDisabledContainer,
                        disabledContentColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                    ),
                    border = BorderStroke(
                        width = if (isSpaceSelected) 2.dp else 1.dp,
                        color = if (isSpaceSelected) correctBorder else MaterialTheme.colorScheme.outline.copy(
                            alpha = 0.5f
                        )
                    ),
                    modifier = Modifier.fillMaxWidth().height(spaceHeight)
                        .semantics { testTag = "␣" }
                ) {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "␣",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
    }
}
